import { useState, useEffect, useCallback, useRef } from "react";
import {
  Flag, RefreshCw, Pencil, CheckCircle2, ChevronDown, ChevronUp,
  Loader2, AlertTriangle, Clock, History, X,
} from "lucide-react";
import {
  getMitraaFlagged,
  refreshMitraaResponse,
  editMitraaResponse,
  resolveMitraaResponse,
} from "@/lib/api";

// ── Draft helpers (localStorage) ──────────────────────────────────────────────
function draftKey(recordId, wrongOption) {
  return `mitraa_draft__${recordId}__${wrongOption || ""}`;
}
function loadDraft(recordId, wrongOption) {
  try { return localStorage.getItem(draftKey(recordId, wrongOption)) || null; }
  catch { return null; }
}
function saveDraft(recordId, wrongOption, text) {
  try { localStorage.setItem(draftKey(recordId, wrongOption), text); } catch {}
}
function clearDraft(recordId, wrongOption) {
  try { localStorage.removeItem(draftKey(recordId, wrongOption)); } catch {}
}

// ── Timestamp display ──────────────────────────────────────────────────────────
function Ts({ iso }) {
  if (!iso) return <span className="text-slate-400">—</span>;
  const d = new Date(iso);
  return (
    <span className="tabular-nums text-xs text-slate-500">
      {d.toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "2-digit" })}{" "}
      {d.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" })}
    </span>
  );
}

// ── Subject badge ──────────────────────────────────────────────────────────────
const SUBJECT_COLORS = {
  Mathematics: { bg: "#eff6ff", text: "#1d4ed8" },
  English:     { bg: "#f0fdf4", text: "#166534" },
  Hindi:       { bg: "#fff7ed", text: "#9a3412" },
  EVS:         { bg: "#f0fdfa", text: "#134e4a" },
};
function SubjectBadge({ subject }) {
  const c = SUBJECT_COLORS[subject] || { bg: "#f1f5f9", text: "#475569" };
  return (
    <span
      className="inline-block px-2 py-0.5 rounded text-xs font-semibold"
      style={{ background: c.bg, color: c.text }}
    >
      {subject || "—"}
    </span>
  );
}

// ── Edit history entry ─────────────────────────────────────────────────────────
function EditHistoryEntry({ entry, index, total }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="border rounded-lg overflow-hidden"
      style={{ borderColor: "#e2e8f0" }}
    >
      <button
        type="button"
        className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-slate-50 transition-colors"
        onClick={() => setOpen(!open)}
      >
        <span className="text-xs text-slate-600 font-medium">
          Edit {total - index} — <Ts iso={entry.edited_at} />
        </span>
        {open ? <ChevronUp size={12} className="text-slate-400" /> : <ChevronDown size={12} className="text-slate-400" />}
      </button>
      {open && (
        <div className="px-3 pb-3 pt-0">
          <p className="text-xs text-slate-400 mb-1">Previous text:</p>
          <p className="text-xs text-slate-700 bg-slate-50 rounded p-2 leading-relaxed">{entry.previous_text}</p>
        </div>
      )}
    </div>
  );
}

// ── Variant card (inside expanded row) ────────────────────────────────────────
function VariantCard({ variant, recordId, token, onUpdated, wrongOptionChosen }) {
  const [editing, setEditing] = useState(false);
  const [draftRestored, setDraftRestored] = useState(false);
  const [editText, setEditText] = useState("");
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [confirmRefresh, setConfirmRefresh] = useState(false);
  const [editError, setEditError] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const textareaRef = useRef(null);

  const startEdit = () => {
    const draft = loadDraft(recordId, wrongOptionChosen);
    if (draft && draft !== variant.text) {
      setEditText(draft);
      setDraftRestored(true);
    } else {
      setEditText(variant.text || "");
      setDraftRestored(false);
    }
    setEditing(true);
    setEditError(null);
    setTimeout(() => textareaRef.current?.focus(), 50);
  };

  const cancelEdit = () => {
    setEditing(false);
    setDraftRestored(false);
    setEditError(null);
  };

  const discardDraft = () => {
    clearDraft(recordId, wrongOptionChosen);
    setEditText(variant.text || "");
    setDraftRestored(false);
  };

  const handleTextChange = (val) => {
    setEditText(val);
    saveDraft(recordId, wrongOptionChosen, val);
  };

  const handleSave = async () => {
    if (!editText.trim()) return;
    setSaving(true);
    setEditError(null);
    try {
      await editMitraaResponse(
        { record_id: recordId, wrong_option_chosen: wrongOptionChosen, old_text: variant.text, new_text: editText.trim() },
        token,
      );
      clearDraft(recordId, wrongOptionChosen);
      setEditing(false);
      setDraftRestored(false);
      onUpdated();
    } catch (e) {
      setEditError(e?.response?.data?.detail || "Save failed. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setConfirmRefresh(false);
    try {
      await refreshMitraaResponse({ record_id: recordId, wrong_option_chosen: wrongOptionChosen }, token);
      onUpdated();
    } catch (e) {
      setEditError(e?.response?.data?.detail || "Refresh failed. Try again.");
      setRefreshing(false);
    }
  };

  const editHistory = variant.edit_history || [];

  return (
    <div
      className="rounded-xl p-4"
      style={{
        background: variant.flagged ? "#fff7ed" : "#f8fafc",
        border: `1.5px solid ${variant.flagged ? "#fed7aa" : "#e2e8f0"}`,
      }}
      data-testid={`variant-card-${variant.flagged ? "flagged" : "clean"}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          {variant.flagged ? (
            <span className="flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: "#fde8d3", color: "#c05621" }}>
              <Flag size={10} /> Flagged
            </span>
          ) : (
            <span className="flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: "#d1fae5", color: "#065f46" }}>
              <CheckCircle2 size={10} /> Clean
            </span>
          )}
          {variant.flagged_at && (
            <span className="text-xs text-slate-400 flex items-center gap-1">
              <Clock size={10} /> <Ts iso={variant.flagged_at} />
            </span>
          )}
        </div>
        {!editing && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={startEdit}
              className="flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors hover:bg-blue-50"
              style={{ borderColor: "#93c5fd", color: "#2563eb" }}
              data-testid="variant-edit-btn"
            >
              <Pencil size={11} /> Edit
            </button>
            <button
              type="button"
              onClick={() => setConfirmRefresh(true)}
              disabled={refreshing}
              className="flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors hover:bg-amber-50"
              style={{ borderColor: "#fcd34d", color: "#b45309" }}
              data-testid="variant-refresh-btn"
            >
              {refreshing ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
              {refreshing ? "Refreshing…" : "Refresh"}
            </button>
          </div>
        )}
      </div>

      {/* Confirm refresh modal */}
      {confirmRefresh && (
        <div
          className="rounded-lg p-3 mb-3 flex flex-col gap-2"
          style={{ background: "#fff3cd", border: "1px solid #fcd34d" }}
        >
          <p className="text-xs font-medium text-amber-900 flex items-center gap-1.5">
            <AlertTriangle size={13} />
            This will delete the flagged response and generate a fresh one. Continue?
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleRefresh}
              className="text-xs font-semibold px-3 py-1.5 rounded-lg text-white"
              style={{ background: "#d97706" }}
              data-testid="confirm-refresh-btn"
            >
              Yes, Refresh
            </button>
            <button
              type="button"
              onClick={() => setConfirmRefresh(false)}
              className="text-xs font-semibold px-3 py-1.5 rounded-lg border"
              style={{ borderColor: "#d1d5db", color: "#6b7280" }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Response text or edit area */}
      {editing ? (
        <div className="flex flex-col gap-2">
          {draftRestored && (
            <div
              className="flex items-center justify-between text-xs px-3 py-2 rounded-lg"
              style={{ background: "#fefce8", border: "1px solid #fde047", color: "#854d0e" }}
            >
              <span className="font-medium flex items-center gap-1"><AlertTriangle size={11} /> Draft restored from previous session</span>
              <button type="button" onClick={discardDraft} className="underline hover:no-underline" data-testid="discard-draft-btn">Discard</button>
            </div>
          )}
          <textarea
            ref={textareaRef}
            value={editText}
            onChange={(e) => handleTextChange(e.target.value)}
            rows={5}
            className="w-full text-sm rounded-lg px-3 py-2 resize-y focus:outline-none focus:ring-2"
            style={{ border: "1.5px solid #93c5fd", lineHeight: 1.6, focusRingColor: "#3b82f6" }}
            data-testid="edit-textarea"
          />
          {editError && (
            <p className="text-xs font-medium text-red-700 bg-red-50 rounded-lg px-3 py-2">{editError}</p>
          )}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || !editText.trim()}
              className="flex items-center gap-1 text-xs font-semibold px-4 py-1.5 rounded-lg text-white"
              style={{ background: saving ? "#93c5fd" : "#2563eb" }}
              data-testid="save-edit-btn"
            >
              {saving ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />}
              {saving ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              onClick={cancelEdit}
              className="text-xs font-medium px-3 py-1.5 rounded-lg border"
              style={{ borderColor: "#d1d5db", color: "#6b7280" }}
              data-testid="cancel-edit-btn"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-slate-800 leading-relaxed">{variant.text}</p>
      )}

      {/* Edit history */}
      {editHistory.length > 0 && !editing && (
        <div className="mt-3">
          <button
            type="button"
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 font-medium"
            data-testid="toggle-history-btn"
          >
            <History size={11} />
            Edit history ({editHistory.length})
            {showHistory ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
          </button>
          {showHistory && (
            <div className="mt-2 flex flex-col gap-2">
              {[...editHistory].reverse().map((entry, i) => (
                <EditHistoryEntry key={i} entry={entry} index={i} total={editHistory.length} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Expanded row ───────────────────────────────────────────────────────────────
function ExpandedRow({ record, token, onResolved, onUpdated }) {
  const [resolving, setResolving] = useState(false);

  // Get the flagged wrong option from the first flagged variant
  const flaggedOption = record.explanation_simple_flagged_option
    || record.variants?.find(v => v.flagged)?.wrong_option_chosen
    || null;

  const handleResolve = async () => {
    setResolving(true);
    try {
      await resolveMitraaResponse(
        { record_id: record.record_id, wrong_option_chosen: flaggedOption },
        token,
      );
      onResolved(record.record_id);
    } catch (e) {
      setResolving(false);
    }
  };

  return (
    <div className="px-6 pb-6 pt-2" data-testid="expanded-row">
      {/* Full question */}
      <div className="mb-4">
        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Full Question</p>
        <p className="text-sm text-slate-800 leading-relaxed">{record.question_text}</p>
      </div>

      {/* Flagged option */}
      {flaggedOption && (
        <div className="mb-4">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Student's Wrong Choice</p>
          <span
            className="inline-block text-sm px-3 py-1 rounded-lg font-medium"
            style={{ background: "#fff7ed", color: "#c05621", border: "1px solid #fed7aa" }}
          >
            {flaggedOption}
          </span>
        </div>
      )}

      {/* Variants */}
      <div className="mb-4">
        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
          Cached Responses ({record.variants?.length || 0})
        </p>
        <div className="flex flex-col gap-3">
          {(record.variants || []).map((v, i) => (
            <VariantCard
              key={i}
              variant={v}
              recordId={record.record_id}
              wrongOptionChosen={v.wrong_option_chosen || flaggedOption}
              token={token}
              onUpdated={onUpdated}
            />
          ))}
          {(!record.variants || record.variants.length === 0) && (
            <p className="text-sm text-slate-400 italic">No cached variants found.</p>
          )}
        </div>
      </div>

      {/* Resolve button */}
      <div
        className="flex items-center justify-between pt-4"
        style={{ borderTop: "1px solid #f1f5f9" }}
      >
        <p className="text-xs text-slate-500">Resolve clears all flags for this record and removes it from this list.</p>
        <button
          type="button"
          onClick={handleResolve}
          disabled={resolving}
          className="flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-lg text-white"
          style={{ background: resolving ? "#86efac" : "#16a34a" }}
          data-testid="resolve-btn"
        >
          {resolving ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
          {resolving ? "Resolving…" : "Resolve"}
        </button>
      </div>
    </div>
  );
}

// ── Table row ──────────────────────────────────────────────────────────────────
function TableRow({ record, token, onResolved, onUpdated }) {
  const [expanded, setExpanded] = useState(false);

  const flaggedVariant = record.variants?.find(v => v.flagged);
  const flaggedOption = record.explanation_simple_flagged_option
    || flaggedVariant?.wrong_option_chosen
    || "—";
  const displayText = flaggedVariant?.text || record.variants?.[0]?.text || "—";

  return (
    <div
      className="rounded-xl overflow-hidden transition-shadow"
      style={{
        border: "1.5px solid #e2e8f0",
        boxShadow: expanded ? "0 4px 16px rgba(0,0,0,0.07)" : "none",
        background: "#fff",
      }}
      data-testid="flagged-row"
    >
      {/* Summary row */}
      <button
        type="button"
        className="w-full text-left px-6 py-4 hover:bg-slate-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
        data-testid="row-expand-btn"
      >
        <div className="grid gap-3" style={{ gridTemplateColumns: "1fr auto auto auto auto auto" }}>
          {/* Question */}
          <div className="flex flex-col gap-1 min-w-0">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Question</p>
            <p className="text-sm text-slate-800 truncate" title={record.question_text}>
              {(record.question_text || "").slice(0, 60)}{record.question_text?.length > 60 ? "…" : ""}
            </p>
          </div>
          {/* Grade */}
          <div className="flex flex-col gap-1 items-center">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Grade</p>
            <span className="text-sm font-bold text-slate-700">
              {String(record.grade || "").replace("Grade ", "G")}
            </span>
          </div>
          {/* Subject */}
          <div className="flex flex-col gap-1 items-center">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Subject</p>
            <SubjectBadge subject={record.subject} />
          </div>
          {/* Flagged option */}
          <div className="flex flex-col gap-1 items-center max-w-[140px]">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Flagged Option</p>
            <span className="text-xs text-amber-700 font-medium truncate" title={flaggedOption}>
              {(flaggedOption || "—").slice(0, 30)}{flaggedOption?.length > 30 ? "…" : ""}
            </span>
          </div>
          {/* Response preview */}
          <div className="flex flex-col gap-1 max-w-[220px]">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Response</p>
            <p className="text-xs text-slate-600 truncate" title={displayText}>
              {(displayText || "—").slice(0, 80)}{displayText?.length > 80 ? "…" : ""}
            </p>
          </div>
          {/* Expand toggle */}
          <div className="flex items-center self-center ml-2">
            {expanded
              ? <ChevronUp size={16} className="text-slate-400" />
              : <ChevronDown size={16} className="text-slate-400" />
            }
          </div>
        </div>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div style={{ borderTop: "1px solid #f1f5f9" }}>
          <ExpandedRow
            record={record}
            token={token}
            onResolved={(id) => { setExpanded(false); onResolved(id); }}
            onUpdated={onUpdated}
          />
        </div>
      )}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────
export default function MitraaResponses({ token }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getMitraaFlagged(token);
      setRecords(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load flagged responses.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleResolved = (recordId) => {
    setRecords(prev => prev.filter(r => r.record_id !== recordId));
  };

  return (
    <div className="px-6 py-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1
              className="font-bold text-slate-900"
              style={{ fontSize: "22px" }}
              data-testid="mitraa-responses-title"
            >
              Mitraa Responses
            </h1>
            {!loading && records.length > 0 && (
              <span
                className="text-xs font-bold px-2.5 py-1 rounded-full"
                style={{ background: "#fee2e2", color: "#991b1b" }}
                data-testid="flagged-count-badge"
              >
                {records.length} flagged
              </span>
            )}
          </div>
          <p className="text-sm text-slate-500">
            Flagged student explanations — review, edit, or refresh.
          </p>
        </div>
        <button
          type="button"
          onClick={fetchData}
          disabled={loading}
          className="flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-lg border transition-colors hover:bg-slate-50"
          style={{ borderColor: "#d1d5db", color: "#374151" }}
          data-testid="refresh-list-btn"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          Refresh
        </button>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={32} className="animate-spin text-blue-500" />
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div
          className="flex items-center gap-3 rounded-xl px-5 py-4"
          style={{ background: "#fef2f2", border: "1px solid #fca5a5" }}
        >
          <AlertTriangle size={18} className="text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700 font-medium">{error}</p>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && records.length === 0 && (
        <div
          className="flex flex-col items-center justify-center py-20 rounded-2xl"
          style={{ background: "#f8fafc", border: "1.5px dashed #cbd5e1" }}
          data-testid="empty-state"
        >
          <CheckCircle2 size={40} className="text-emerald-400 mb-3" />
          <p className="text-base font-semibold text-slate-700 mb-1">No flagged responses. All good! ✓</p>
          <p className="text-sm text-slate-400">Students haven't flagged any Mitraa explanations yet.</p>
        </div>
      )}

      {/* Table */}
      {!loading && !error && records.length > 0 && (
        <div className="flex flex-col gap-3" data-testid="flagged-table">
          {records.map((record) => (
            <TableRow
              key={record.record_id}
              record={record}
              token={token}
              onResolved={handleResolved}
              onUpdated={fetchData}
            />
          ))}
        </div>
      )}
    </div>
  );
}
